import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { CalendarIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface DatePickerProps {
  value: Date | string | null | { start: Date | string | null; end: Date | string | null };
  onChange: (date: Date | { start: Date | null; end: Date | null }) => void;
  className?: string;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  isRange?: boolean;
  startPlaceholder?: string;
  endPlaceholder?: string;
  label?: string;
  helperText?: string;
  isRequired?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const inputStyles = {
  base: 'py-3 px-4 text-base',
  width: 'w-full max-w-[220px]',
  rangeWidth: 'w-full max-w-[400px]',
  icon: 'w-5 h-5'
};

// Add helper functions for date handling
function parseDate(date: Date | string | null): Date | null {
  if (!date) return null;
  if (date instanceof Date) return date;
  
  // Try parsing ISO string or timestamptz
  const parsedDate = new Date(date);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function isSameDate(date1: Date | string | null, date2: Date | string | null): boolean {
  if (!date1 || !date2) return false;
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  if (!parsed1 || !parsed2) return false;
  return parsed1.toDateString() === parsed2.toDateString();
}

function parseDateRange(value: { start: Date | string | null; end: Date | string | null } | null) {
  if (!value) return { start: null, end: null };
  return {
    start: parseDate(value.start),
    end: parseDate(value.end)
  };
}

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  className,
  error,
  placeholder = 'Select date',
  startPlaceholder = 'Start date',
  endPlaceholder = 'End date',
  disabled,
  isRange = false,
  label,
  helperText,
  isRequired = false,
  minDate,
  maxDate
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarReadyToShow, setIsCalendarReadyToShow] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    if (isRange) {
      const rangeValue = parseDateRange(value as { start: Date | string | null; end: Date | string | null });
      return rangeValue.start || new Date();
    }
    return parseDate(value as Date | string) || new Date();
  });
  const [focusedDate, setFocusedDate] = useState<Date | null>(null);
  const [rangeSelectionStep, setRangeSelectionStep] = useState<'start' | 'end'>('start');
  const containerRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const [calendarPosition, setCalendarPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [validationError, setValidationError] = useState<string | null>(null);

  const calculateAndSetPosition = useCallback(() => {
    if (!containerRef.current || !calendarRef.current || !isOpen) {
        setIsCalendarReadyToShow(false);
        return;
    }

    const inputRect = containerRef.current.getBoundingClientRect();
    const calendarElem = calendarRef.current;

    const calHeight = calendarElem.offsetHeight > 0 ? calendarElem.offsetHeight : 400; // Use actual if available, else estimate
    const calWidth = calendarElem.offsetWidth > 0 ? calendarElem.offsetWidth : 360;   // Use actual if available, else estimate

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = inputRect.bottom; // Default: below input
    let left = inputRect.left;  // Default: align left with input

    // Try to open below first
    if (top + calHeight > viewportHeight) { // If it overflows below
        if (inputRect.top - calHeight > 0) { // And there's space above
            top = inputRect.top - calHeight; // Open above
        } else { // Not enough space above or below, try to fit by aligning to bottom of viewport
            top = Math.max(5, viewportHeight - calHeight - 5); // 5px padding
        }
    }

    // Try to align left with input first
    if (left + calWidth > viewportWidth) { // If it overflows right
        if (inputRect.right - calWidth > 0) { // And there's space to align right of input
            left = inputRect.right - calWidth;
        } else { // Not enough space left or right, align to right of viewport
            left = Math.max(5, viewportWidth - calWidth - 5); // 5px padding
        }
    }
    
    // Final clamp to viewport boundaries
    if (top < 0) top = 5;
    if (left < 0) left = 5;
    // These checks ensure that if clamping one dimension pushes it out on another, it's re-clamped.
    if (top + calHeight > viewportHeight) top = Math.max(5, viewportHeight - calHeight - 5);
    if (left + calWidth > viewportWidth) left = Math.max(5, viewportWidth - calWidth - 5);

    setCalendarPosition({
        top: top + window.scrollY,
        left: left + window.scrollX
    });
    setIsCalendarReadyToShow(true);
  }, [isOpen]);

  const toggleCalendar = () => {
    setIsOpen(prev => {
      if (!prev) {
        setIsCalendarReadyToShow(false);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
          calculateAndSetPosition();
      }, 0);
      return () => clearTimeout(timer);
    } else {
      setIsCalendarReadyToShow(false);
    }
  }, [isOpen, viewDate, calculateAndSetPosition]);

  const formatDate = (date: Date | string | null): string => {
    const parsedDate = parseDate(date);
    if (!parsedDate) return '';
    
    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatRangeValue = () => {
    const rangeValue = value as { start: Date | string | null; end: Date | string | null };
    if (!rangeValue) return { start: null, end: null };
    
    return {
      start: formatDate(rangeValue.start),
      end: formatDate(rangeValue.end)
    };
  };

  const getDaysInMonth = (date: Date): Date[] => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];

    // Add previous month's days
    for (let i = 0; i < firstDay.getDay(); i++) {
      const prevDate = new Date(year, month, -i);
      days.unshift(prevDate);
    }

    // Add current month's days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    // Add next month's days to complete the grid
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(new Date(year, month + 1, i));
    }

    return days;
  };

  const isDateDisabled = (date: Date): boolean => {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  };

  const validateDateSelection = (date: Date): string | null => {
    if (minDate && date < minDate) {
      return `Date cannot be before ${formatDate(minDate)}`;
    }
    if (maxDate && date > maxDate) {
      return `Date cannot be after ${formatDate(maxDate)}`;
    }
    return null;
  };

  const handleDateSelect = (date: Date) => {
    const error = validateDateSelection(date);
    if (error) {
      setValidationError(error);
      return;
    }

    if (!isRange) {
      onChange(date);
      setValidationError(null);
      setIsOpen(false);
      return;
    }

    const rangeValue = parseDateRange(value as { start: Date | string | null; end: Date | string | null });
    
    if (rangeSelectionStep === 'start') {
      onChange({ start: date, end: null });
      setRangeSelectionStep('end');
      setValidationError(null);
    } else {
      // Ensure end date is not before start date
      if (rangeValue.start && date < rangeValue.start) {
        setValidationError('End date cannot be before start date');
        return;
      }
      onChange({ start: rangeValue.start, end: date });
      setIsOpen(false);
      setRangeSelectionStep('start');
      setValidationError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!focusedDate) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        setFocusedDate(new Date(focusedDate.setDate(focusedDate.getDate() - 1)));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setFocusedDate(new Date(focusedDate.setDate(focusedDate.getDate() + 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedDate(new Date(focusedDate.setDate(focusedDate.getDate() - 7)));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedDate(new Date(focusedDate.setDate(focusedDate.getDate() + 7)));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        handleDateSelect(focusedDate);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current && 
        !containerRef.current.contains(event.target as Node) &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node) 
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) { // Only add listener if calendar is open
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]); // Depend on isOpen to add/remove listener

  useEffect(() => {
    if (isOpen && !focusedDate) {
      if (isRange) {
        const rangeValue = parseDateRange(value as { start: Date | string | null; end: Date | string | null });
        setFocusedDate(rangeValue.start || new Date());
      } else {
        setFocusedDate(parseDate(value as Date | string) || new Date());
      }
    }
  }, [isOpen, value]);

  useEffect(() => {
    const updatePos = () => {
      if (isOpen) { // Check isOpen inside the handler
        calculateAndSetPosition();
      }
    };

    if (isOpen) {
      window.addEventListener('resize', updatePos);
      window.addEventListener('scroll', updatePos, true); // true for capture phase

      // Attempt to find a scrollable modal parent
      const scrollableModalParent = containerRef.current?.closest('.fixed.inset-0.z-10.overflow-y-auto');
      if (scrollableModalParent) {
        scrollableModalParent.addEventListener('scroll', updatePos);
      }

      return () => {
        window.removeEventListener('resize', updatePos);
        window.removeEventListener('scroll', updatePos, true);
        if (scrollableModalParent) {
          scrollableModalParent.removeEventListener('scroll', updatePos);
        }
      };
    }
  }, [isOpen, calculateAndSetPosition]); // Add calculateAndSetPosition as dependency

  const isDateInRange = (date: Date) => {
    if (!isRange) return false;
    const rangeValue = parseDateRange(value as { start: Date | string | null; end: Date | string | null });
    if (!rangeValue.start || !rangeValue.end) return false;
    
    const parsedStart = parseDate(rangeValue.start);
    const parsedEnd = parseDate(rangeValue.end);
    if (!parsedStart || !parsedEnd) return false;
    
    return date >= parsedStart && date <= parsedEnd;
  };

  const isStartDate = (date: Date) => {
    if (!isRange) return false;
    const rangeValue = parseDateRange(value as { start: Date | string | null; end: Date | string | null });
    return isSameDate(rangeValue.start, date);
  };

  const isEndDate = (date: Date) => {
    if (!isRange) return false;
    const rangeValue = parseDateRange(value as { start: Date | string | null; end: Date | string | null });
    return isSameDate(rangeValue.end, date);
  };

  return (
    <div ref={containerRef} className={clsx("relative", className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div
        onClick={() => !disabled && toggleCalendar()}
        className={clsx(
          'flex items-center justify-between rounded-lg border border-gray-300 shadow-xs',
          'focus:border-primary-base focus:outline-hidden focus:ring-1 focus:ring-primary-base',
          (error || validationError) ? 'border-red-300' : 'hover:border-primary-base',
          disabled ? 'bg-gray-50 cursor-not-allowed' : 'cursor-pointer',
          inputStyles.base,
          isRange ? inputStyles.rangeWidth : inputStyles.width,
          className
        )}
      >
        {isRange ? (
          <div className="flex flex-1 items-center justify-between">
            <div className="flex-1 min-w-0 px-2">
              {formatRangeValue().start ? (
                <span>{formatRangeValue().start}</span>
              ) : (
                <span className="text-gray-400">{startPlaceholder}</span>
              )}
            </div>
            <div className="px-2 text-gray-400">→</div>
            <div className="flex-1 min-w-0 px-2">
              {formatRangeValue().end ? (
                <span>{formatRangeValue().end}</span>
              ) : (
                <span className="text-gray-400">{endPlaceholder}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1">
            {value ? formatDate(value as Date | string) : <span className="text-gray-400">{placeholder}</span>}
          </div>
        )}
        <CalendarIcon 
          className={clsx(
            'ml-2 shrink-0',
            inputStyles.icon,
            disabled ? 'text-gray-400' : 'text-gray-500',
            !disabled && 'group-hover:text-primary-base'
          )}
        />
      </div>

      {/* Calendar Popup - Conditionally Rendered with Portal */}
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          ref={calendarRef}
          className={clsx(
            'z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 p-4',
            'absolute',
            'max-w-[360px]',
            'transition-opacity duration-75 ease-out'
          )}
          style={{
            top: `${calendarPosition.top}px`,
            left: `${calendarPosition.left}px`,
            opacity: isCalendarReadyToShow ? 1 : 0,
          }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="small" 
              onClick={() => setViewDate(new Date(viewDate.setMonth(viewDate.getMonth() - 1)))}
              aria-label="Previous month"
            >
              &lt;
            </Button>
            <div className="text-lg font-semibold">
              {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
            </div>
            <Button 
              variant="ghost" 
              size="small" 
              onClick={() => setViewDate(new Date(viewDate.setMonth(viewDate.getMonth() + 1)))}
              aria-label="Next month"
            >
              &gt;
            </Button>
          </div>

          {/* Days Grid */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {DAYS.map(day => (
              <div key={day} className="text-xs font-medium text-gray-500">{day}</div>
            ))}
            {getDaysInMonth(viewDate).map((date, index) => {
              const isDisabled = isDateDisabled(date);
              const isSelected = !isRange && isSameDate(date, value as Date | string);
              const isRangeStart = isRange && isStartDate(date);
              const isRangeEnd = isRange && isEndDate(date);
              const isInRange = isRange && isDateInRange(date);
              const isFocused = isSameDate(date, focusedDate);
              const isCurrentMonth = date.getMonth() === viewDate.getMonth();
              
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => !isDisabled && handleDateSelect(date)}
                  onMouseEnter={() => setFocusedDate(date)} 
                  onFocus={() => setFocusedDate(date)}
                  disabled={isDisabled}
                  className={clsx(
                    'p-2 rounded-full text-sm',
                    {
                      'bg-primary-base text-white': isSelected || isRangeStart || isRangeEnd,
                      'bg-primary-100 text-primary-700': isInRange && !isSelected && !isRangeStart && !isRangeEnd,
                      'hover:bg-gray-100': !isDisabled && !isSelected && !isInRange && !isRangeStart && !isRangeEnd,
                      'text-gray-400 cursor-not-allowed': isDisabled,
                      'text-gray-900': !isDisabled && isCurrentMonth,
                      'text-gray-300': !isDisabled && !isCurrentMonth,
                      'ring-2 ring-primary-base ring-offset-1': isFocused,
                    }
                  )}
                  aria-pressed={isSelected || isRangeStart || isRangeEnd}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Validation Error Message */}
          {validationError && (
            <p className="mt-3 text-sm text-red-600 text-center">{validationError}</p>
          )}
        </div>,
        document.body
      )}

      {helperText && (
        <p className={clsx('mt-2 text-sm', error ? 'text-red-600' : 'text-gray-500')}>{helperText}</p>
      )}
    </div>
  );
};

DatePicker.displayName = 'DatePicker'; 